import {
  ICellRendererParams,
  ICellEditor,
  ICellEditorParams,
} from "ag-grid-community";
import Editor from "@monaco-editor/react";
import { createRoot } from "react-dom/client";
import { createElement } from "react";

export class EnhancedJsonCellEditor implements ICellEditor {
  private gui: HTMLElement;
  private value: string = "{}";
  private reactRoot: ReturnType<typeof createRoot> | null = null;

  constructor() {
    // Create container that matches AG Grid's large text editor popup style
    this.gui = document.createElement("div");
    this.gui.className = "ag-large-text-input monaco-json-editor";

    // Create title bar like AG Grid's large text editor
    const titleBar = document.createElement("div");
    titleBar.className = "monaco-editor-title";
    titleBar.textContent = "JSON Editor";

    // Create Monaco container
    const editorContainer = document.createElement("div");
    editorContainer.className = "monaco-editor-container";

    // Create footer with helpful text like AG Grid's large text editor
    const footer = document.createElement("div");
    footer.className = "monaco-editor-footer";
    footer.textContent =
      "Shift+Enter for new line • Tab for indentation • Esc to cancel • Enter to save";

    this.gui.appendChild(titleBar);
    this.gui.appendChild(editorContainer);
    this.gui.appendChild(footer);
  }

  init(params: ICellEditorParams): void {
    // Treat all values as JSON strings for consistent handling
    const initialValue = params.value;
    if (typeof initialValue === "string") {
      this.value = this.formatJson(initialValue);
    } else {
      // Convert objects to formatted JSON strings
      this.value = this.formatJson(JSON.stringify(initialValue || {}, null, 2));
    }

    const editorContainer = this.gui.children[1] as HTMLElement;
    this.reactRoot = createRoot(editorContainer);

    // Determine theme based on current theme
    const isDarkMode =
      document.documentElement.getAttribute("data-theme") === "dark" ||
      document.documentElement.classList.contains("dark") ||
      window.matchMedia("(prefers-color-scheme: dark)").matches;

    const MonacoEditor = createElement(Editor, {
      width: "100%",
      height: "100%",
      language: "json",
      value: this.value,
      theme: isDarkMode ? "vs-dark" : "light",
      options: {
        // Core editing options
        minimap: { enabled: false },
        scrollBeyondLastLine: false,
        automaticLayout: true,
        lineNumbers: "on",
        renderValidationDecorations: "on",
        formatOnPaste: true,
        formatOnType: true,

        // Styling to match AG Grid
        fontSize: 13,
        fontFamily: "var(--default-font-family)",
        tabSize: 2,
        insertSpaces: true,
        wordWrap: "on",

        // Scrollbar styling
        scrollbar: {
          verticalScrollbarSize: 8,
          horizontalScrollbarSize: 8,
        },

        // Disable features that conflict with AG Grid's popup behavior
        acceptSuggestionOnEnter: "off",
        quickSuggestions: false,
        parameterHints: { enabled: false },
        suggestOnTriggerCharacters: false,
        tabCompletion: "off",
        wordBasedSuggestions: "off",
        contextmenu: false,
        links: false,
        overviewRulerLanes: 0,
      },
      onChange: (value: string | undefined) => {
        this.value = value || "{}";
      },
      onMount: (editor) => {
        // Focus editor immediately
        setTimeout(() => editor.focus(), 50);

        // Let Monaco handle all key events naturally
        // AG Grid will handle Enter/Escape through the popup editor lifecycle
      },
      onValidate: (_markers) => {
        // Show validation errors but don't prevent saving
      },
    });

    this.reactRoot.render(MonacoEditor);
  }

  private formatJson(value: string): string {
    try {
      if (!value || value.trim() === "") {
        return "{}";
      }
      const parsed = typeof value === "string" ? JSON.parse(value) : value;
      return JSON.stringify(parsed, null, 2);
    } catch {
      // Return as-is if it's not valid JSON - let Monaco show syntax errors
      return value || "{}";
    }
  }

  getGui(): HTMLElement {
    return this.gui;
  }

  getValue(): string {
    return this.value || "{}";
  }

  destroy(): void {
    if (this.reactRoot) {
      // Defer unmounting to avoid React lifecycle conflicts
      setTimeout(() => {
        try {
          this.reactRoot?.unmount();
        } catch (error) {
          console.warn("Error unmounting Monaco editor:", error);
        }
      }, 0);
    }
  }

  // Make it a popup editor like AG Grid's large text editor
  isPopup(): boolean {
    return true;
  }

  // Allow canceling before and after start
  isCancelBeforeStart?(): boolean {
    return false;
  }

  isCancelAfterEnd?(): boolean {
    return false;
  }

  // Note: suppressKeyboardEvent is now handled via cellEditorParams in column definition
}

// Multi-select cell renderer component
export const MultiSelectCellRenderer = (params: ICellRendererParams) => {
  const value = params.value;
  const selectOptions = params.colDef?.cellRendererParams?.selectOptions || [];

  if (!value || value === "" || (Array.isArray(value) && value.length === 0)) {
    return createElement(
      "span",
      { style: { color: "#999", fontStyle: "italic" } },
      "No selection",
    );
  }

  // Handle both array and comma-separated string values
  let selectedValues: string[] = [];

  if (Array.isArray(value)) {
    selectedValues = value.map((v) => String(v));
  } else if (typeof value === "string") {
    // Handle comma-separated values or single value
    selectedValues = value.includes(",")
      ? value.split(",").map((v) => v.trim())
      : [value];
  } else {
    selectedValues = [String(value)];
  }

  // Get labels for selected values
  const selectedLabels = selectedValues
    .map((val) => {
      const option = selectOptions.find(
        (opt: { value: string; label: string }) =>
          String(opt.value) === String(val),
      );
      return option ? option.label : val;
    })
    .filter((label) => label && label !== "");

  if (selectedLabels.length === 0) {
    return createElement(
      "span",
      { style: { color: "#999", fontStyle: "italic" } },
      "No selection",
    );
  }

  // For single selection, show as a subtle chip
  if (selectedLabels.length === 1) {
    return createElement(
      "div",
      {
        style: {
          cursor: "pointer",
          padding: "4px 8px",
          backgroundColor: "var(--blue-3)",
          color: "var(--blue-11)",
          borderRadius: "6px",
          fontSize: "12px",
          fontWeight: "500",
          display: "inline-block",
          border: "1px solid var(--blue-6)",
        },
      },
      selectedLabels[0],
    );
  }

  // For multiple selections, show count + preview
  if (selectedLabels.length <= 2) {
    // Show individual badges for 2 or fewer items
    const badges = selectedLabels.map((label, index) =>
      createElement(
        "span",
        {
          key: index,
          style: {
            padding: "2px 6px",
            backgroundColor: "var(--blue-3)",
            color: "var(--blue-11)",
            borderRadius: "4px",
            fontSize: "11px",
            fontWeight: "500",
            marginRight: index < selectedLabels.length - 1 ? "4px" : "0",
            border: "1px solid var(--blue-6)",
            display: "inline-block",
          },
        },
        label,
      ),
    );

    return createElement(
      "div",
      {
        style: {
          display: "flex",
          alignItems: "center",
          gap: "4px",
          cursor: "pointer",
        },
      },
      ...badges,
    );
  } else {
    // For 3+ items, show count + first item
    return createElement(
      "div",
      {
        style: {
          display: "flex",
          alignItems: "center",
          gap: "6px",
          cursor: "pointer",
        },
      },
      [
        createElement(
          "span",
          {
            key: "first",
            style: {
              padding: "2px 6px",
              backgroundColor: "var(--blue-3)",
              color: "var(--blue-11)",
              borderRadius: "4px",
              fontSize: "11px",
              fontWeight: "500",
              border: "1px solid var(--blue-6)",
              display: "inline-block",
            },
          },
          selectedLabels[0],
        ),
        createElement(
          "span",
          {
            key: "count",
            style: {
              padding: "2px 6px",
              backgroundColor: "var(--gray-4)",
              color: "var(--gray-11)",
              borderRadius: "4px",
              fontSize: "10px",
              fontWeight: "600",
              border: "1px solid var(--gray-6)",
              display: "inline-block",
            },
          },
          `+${selectedLabels.length - 1}`,
        ),
      ],
    );
  }
};
